import React from 'react';
import { mockData } from '../data/mockData';
import useAnalytics from '../hooks/useAnalytics';
import { GitHubIcon, LinkedInIcon, TwitterIcon, MediumIcon } from './Icons';

const Footer = () => {
    const { trackSocialClick, trackContactInteraction } = useAnalytics();
    const currentYear = new Date().getFullYear();

    const handleSocialClick = (platform, url) => {
        trackSocialClick(`footer_${platform}`, url);
    };

    const handleEmailClick = () => {
        trackContactInteraction('footer_email', mockData.personalInfo.email);
    };

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const socialLinks = [
        { name: 'GitHub', icon: GitHubIcon, url: mockData.personalInfo.socialLinks?.github },
        { name: 'LinkedIn', icon: LinkedInIcon, url: mockData.personalInfo.socialLinks?.linkedin },
        { name: 'Twitter', icon: TwitterIcon, url: mockData.personalInfo.socialLinks?.twitter },
        { name: 'Medium', icon: MediumIcon, url: mockData.personalInfo.socialLinks?.medium },
    ];

    return (
        <footer className="bg-white border-t border-border-light pt-8">
            {/* Main Footer */}
            <div className="container py-20 px-6 md:px-8 lg:px-12" >
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12  pb-8">
                    {/* Brand */}
                    <div className="lg:col-span-2">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 bg-coral-500 rounded-full flex items-center justify-center">
                                <span className="text-white font-semibold">M</span>
                            </div>
                            <span className="font-serif text-xl text-charcoal-900">{mockData.personalInfo.name}</span>
                        </div>
                        <p className="text-charcoal-600 mb-6 max-w-md">
                            Software Engineer building scalable mobile and web applications. 
                            Let's build something together.
                        </p>

                        {/* Social Links */}
                        <div className="flex items-center gap-3">
                            {socialLinks.map(({ name, icon: Icon, url }) => url && (
                                <a
                                    key={name}
                                    href={url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={() => handleSocialClick(name.toLowerCase(), url)}
                                    className="w-10 h-10 flex items-center justify-center rounded-full bg-cream-100 text-charcoal-600 hover:bg-coral-50 hover:text-coral-600 transition-all duration-200"
                                    aria-label={name}
                                >
                                    <Icon className="w-4 h-4" />
                                </a>
                            ))}
                        </div>
                    </div>

                    {/* Quick Links */}
                    <div>
                        <h4 className="font-serif text-lg text-charcoal-900 mb-4">Quick Links</h4>
                        <nav className="space-y-3">
                            {['About', 'Work', 'Experience', 'Education', 'Blog'].map((item) => (
                                <a
                                    key={item}
                                    href={`#${item.toLowerCase()}`}
                                    className="block text-charcoal-600 hover:text-coral-600 transition-colors"
                                >
                                    {item}
                                </a>
                            ))}
                        </nav>
                    </div>

                    {/* Contact */}
                    <div>
                        <h4 className="font-serif text-lg text-charcoal-900 mb-4">Get in Touch</h4>
                        <div className="space-y-3">
                            <a
                                href={`mailto:${mockData.personalInfo.email}`}
                                onClick={handleEmailClick}
                                className="flex items-center gap-2 text-charcoal-600 hover:text-coral-600 transition-colors"
                            >
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                </svg>
                                <span className="text-sm">{mockData.personalInfo.email}</span>
                            </a>

                            <div className="flex items-center gap-2 text-charcoal-500">
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                                <span className="text-sm">{mockData.personalInfo.location}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Bottom Bar */}
            <div className="border-t border-border-light">
                <div className="container py-8 px-6 md:px-8 lg:px-12">
                    <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                        <p className="text-sm text-charcoal-500">
                            © {currentYear} {mockData.personalInfo.name}. All rights reserved.
                        </p>

                        <div className="flex items-center gap-6">
                            <span className="text-sm text-charcoal-500 flex items-center gap-1">
                                Made with
                                <img
                                    src="/gif/beating_heart.gif"
                                    alt="love"
                                    className="w-5 h-4"
                                    onError={(e) => {
                                        e.target.style.display = 'none';
                                    }}
                                />
                            </span>

                            <button
                                onClick={scrollToTop}
                                className="w-9 h-9 flex items-center justify-center rounded-full bg-cream-100 text-charcoal-600 hover:bg-coral-50 hover:text-coral-600 transition-all duration-200"
                                aria-label="Scroll to top"
                            >
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
